import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";

export const detectAs400ConfirmationsConfigSchema = z.object({
  // Both default to FALSE: opting in is a deliberate act, not a side effect of
  // deploying. An existing workflow whose stored config predates these fields
  // resolves them to false, so the rejection pass stays dormant until someone
  // turns it on — which matters because the OUT file's exact layout is inferred
  // rather than confirmed, and the scan MOVES files it has read.
  detectErrorFiles: z
    .boolean()
    .default(false)
    .describe(
      "Also scan the division folders for AS400 error files (a batch AS400 " +
        "renamed with an OUT prefix instead of picking up), mark those " +
        "submissions as AS400 Error, and move the file to processed/. Off by " +
        "default — enable per environment once the OUT file layout is confirmed.",
    ),
  notifyOnError: z
    .boolean()
    .default(false)
    .describe(
      "Email the S&D team when an AS400 error file is found. Recipients come " +
        "from sd_844_error_notification_emails (Company Locations admin page), " +
        "falling back to the team inbox. Off by default — enable after a period " +
        "of running detectErrorFiles alone.",
    ),
  maxAgeDays: z
    .number()
    .int()
    .min(1)
    .max(365)
    .default(3)
    .describe(
      "Only check submissions sent within this many days. Sweeps run several " +
        "times a day, so anything unconfirmed after a few days is stale — the " +
        "submissions page / detail modal still checks older ones on open.",
    ),
});

export type DetectAs400ConfirmationsConfig = z.infer<
  typeof detectAs400ConfirmationsConfigSchema
>;

/**
 * Detects how AS400 handled the 844 batch files we uploaded — both outcomes.
 *
 * AS400 picks up GEN844 batch files from Azure Blob Storage daily (2 PM and
 * 6 PM Eastern) and signals the pickup by renaming the file — moving it into
 * the division's `processed/` subfolder with an `@@` prefix. This step probes
 * Azure for those renamed blobs and transitions any confirmed submissions
 * `sent` → `as400_confirmed`, logging an AS400_CONFIRMED activity on each
 * submission's timeline.
 *
 * A REJECTED batch is renamed with an `OUT` prefix instead, with the reasons
 * written into that file. The same sweep scans for those, marks the affected
 * submissions `as400_error` (reasons stored on the submission + its timeline),
 * and emails the S&D team — otherwise a rejected batch sits at `sent` forever
 * with no signal at all.
 */
export const detectAs400ConfirmationsStep = defineStepConfig({
  type: "detect_as400_confirmations",
  name: "Detect AS400 844 Pickups",
  description:
    "Checks Azure Blob Storage for renamed GEN844 files: an @@ rename (AS400 " +
    "pickup) confirms the submission, an OUT rename marks it AS400 Error with " +
    "the reasons AS400 gave and notifies the S&D team. Both log a timeline " +
    "activity.",
  category: "Integration",
  libraryModule: "ShipAndDebit",
  inputSchema: z.object({}),
  outputSchema: z.object({
    checkedCount: z
      .number()
      .describe("Number of sent EDI 844 submissions checked"),
    confirmedCount: z
      .number()
      .describe("Number of submissions confirmed as picked up by AS400"),
    errorFilesParsed: z
      .number()
      .describe("Number of AS400 OUT error files read"),
    erroredSubmissionCount: z
      .number()
      .describe("Number of submissions marked as AS400 Error"),
    unmatchedErrorBatchCount: z
      .number()
      .describe(
        "Rejected batches that matched no 844 submission (reported in the email, never dropped)",
      ),
    errorNotified: z
      .boolean()
      .describe("Whether the AS400 error notification email was sent"),
  }),
  configSchema: detectAs400ConfirmationsConfigSchema,
  emits: [] as const,
  execution: {
    startToCloseTimeout: "10 minutes",
    retry: { maximumAttempts: 2 },
  },
});
